import React, { useState, useEffect } from 'react';
import { getMyLeagueLeaderboard, LeaderboardEntry } from '../api/leagues';
import '../styles/global.css';

interface LeaderboardProps {
  leagueId: string;
  leagueName: string;
  currentPlayerId?: string;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ leagueId, leagueName, currentPlayerId }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        // Use player endpoint to get own league leaderboard
        const data = await getMyLeagueLeaderboard();
        setLeaderboard(data.entries);
      } catch (err) {
        setError('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [leagueId]);

  if (loading) {
    return <div className="placeholder-text">Loading leaderboard...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const displayEntries = expanded ? leaderboard : leaderboard.slice(0, 5);
  const myRank = leaderboard.find(e => e.player_id === currentPlayerId);

  return (
    <div className="card">
      <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🏆 {leagueName} Leaderboard</span>
        {myRank && (
          <span style={{ fontSize: '14px', color: '#666' }}>
            Your rank: #{myRank.rank}
          </span>
        )}
      </h3>

      {leaderboard.length === 0 ? (
        <p className="placeholder-text" style={{ marginTop: '10px' }}>
          No players yet. Be the first to earn XP!
        </p>
      ) : (
        <>
          <table style={{ width: '100%', marginTop: '15px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee' }}>
                <th style={{ textAlign: 'left', padding: '8px 5px', width: '60px' }}>Rank</th>
                <th style={{ textAlign: 'left', padding: '8px 5px' }}>Player</th>
                <th style={{ textAlign: 'right', padding: '8px 5px' }}>XP</th>
              </tr>
            </thead>
            <tbody>
              {displayEntries.map((entry) => (
                <tr
                  key={entry.player_id}
                  style={{
                    borderBottom: '1px solid #eee',
                    backgroundColor: entry.player_id === currentPlayerId ? '#fff3cd' : 'transparent'
                  }}
                >
                  <td style={{ padding: '8px 5px' }}>
                    {entry.rank === 1 && '🥇 '}
                    {entry.rank === 2 && '🥈 '}
                    {entry.rank === 3 && '🥉 '}
                    {entry.rank}
                  </td>
                  <td style={{ padding: '8px 5px' }}>
                    {entry.player_username}
                    {entry.player_id === currentPlayerId && ' (you)'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 5px' }}>
                    {entry.total_xp.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {leaderboard.length > 5 && (
            <button
              className="link-btn"
              onClick={() => setExpanded(!expanded)}
              style={{ marginTop: '10px' }}
            >
              {expanded ? 'Show less' : `Show all ${leaderboard.length} players`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default Leaderboard;
